import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** Ensures the showcase image is only forced on the first page paint. */
  static #hasAppliedInitialShowcase = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // First enter of the product page: show media-library first image (showcase),
    // not the selected variant image. Later option clicks keep theme behaviour
    // (variant media first via Liquid re-render + replaceWith).
    if (!MediaGallery.#hasAppliedInitialShowcase) {
      MediaGallery.#hasAppliedInitialShowcase = true;
      const showcaseId = this.dataset.showcaseMediaId;
      // Slideshow initializes on its own rAF; retry until it can accept select().
      this.#selectMediaById(showcaseId, true);
      setTimeout(() => this.#selectMediaById(showcaseId, true), 50);
      setTimeout(() => this.#selectMediaById(showcaseId, true), 200);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Selects a gallery slide/media by Shopify media id.
   * @param {string | undefined} mediaId
   * @param {boolean} [retry]
   * @param {number} [attempt]
   */
  #selectMediaById(mediaId, retry = false, attempt = 0) {
    if (!mediaId) return;

    const mediaEl = this.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
    if (!(mediaEl instanceof HTMLElement)) {
      if (retry && attempt < 12) {
        requestAnimationFrame(() => this.#selectMediaById(mediaId, true, attempt + 1));
      }
      return;
    }

    if (this.dataset.presentation === 'grid') {
      mediaEl.scrollIntoView({ behavior: 'instant', block: 'nearest' });
      return;
    }

    const slide = mediaEl.closest('slideshow-slide');
    if (!(slide instanceof HTMLElement)) return;

    const slides = [...this.querySelectorAll('slideshow-slide')];
    const index = slides.indexOf(slide);
    if (index < 0) return;

    // Index 0 is already the default slideshow start.
    if (index === 0) return;

    slides.forEach((item, i) => {
      if (!item.getAttribute('slide-id')) item.setAttribute('slide-id', String(i));
    });

    const slideshow = this.slideshow || this.querySelector('slideshow-component');
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.setAttribute('initial-slide', String(index));
      slideshow.select(index, undefined, { animate: false });
      return;
    }

    if (retry && attempt < 12) {
      requestAnimationFrame(() => this.#selectMediaById(mediaId, true, attempt + 1));
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
